import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { urlPathConfiguration } from "../../constants/urlPathConfiguration";
import type { MenuItem } from "./types";

/**
 * UUID segment regex.
 *
 * Vue's AsideMenu.vue (line 1094/1206) uses
 * `/\/[a-f\d-]+(-[a-f\d-]+)*(?=\/\w|$)/g`, which is vulnerable to
 * super-linear backtracking (ReDoS): the outer `[a-f\d-]+` and the inner
 * `(-[a-f\d-]+)*` both consume `-`, so a near-miss like
 * `/aaaa-bbbb-cccc-X` (where `X` is not `/word` or end-of-string) makes
 * the engine try every split combinatorially.
 *
 * Since `-` is already in `[a-f\d-]`, the inner repetition is redundant.
 * Collapsing to a single `[a-f\d-]+` matches the same set of strings in
 * linear time — behaviour stays identical to Vue for every input the
 * router actually feeds us, with no DoS surface.
 */
const UUID_SEGMENT_RE = /\/[a-f\d-]+(?=\/\w|$)/g;

/**
 * Strips UUID-like path segments so `/orders/sales-orders/amazon/3a4b-...`
 * becomes `/orders/sales-orders/amazon`.
 */
const stripUuids = (path: string): string => path.replace(UUID_SEGMENT_RE, "");

/**
 * Port of Vue's `isActive(patterns)` (AsideMenu.vue lines 1204-1225).
 *
 * Returns true if the current pathname (after UUID stripping) matches any
 * pattern in the array. Supports `?container-type=` query param matching.
 */
export const isActivePatterns = (
  patterns: string[] | undefined,
  pathname: string,
  searchParams: URLSearchParams,
): boolean => {
  if (!patterns || patterns.length === 0) return false;
  const cleanPath = stripUuids(pathname);
  const containerType = searchParams.get("container-type") ?? "";

  return patterns.some((pattern) => {
    const [pathPattern, queryPattern] = pattern.split("?container-type=");
    const pathMatches = pathPattern === cleanPath;
    const queryMatches =
      queryPattern != null ? queryPattern === containerType : true;
    return pathMatches && queryMatches;
  });
};

/**
 * Port of Vue's `isChildActive(menu, currentPath)` (AsideMenu.vue lines 1048-1060).
 *
 * Recursively checks if any descendant of `menu` has a routeId whose
 * urlPathConfiguration patterns match the current path.
 */
export const isChildActive = (
  menu: MenuItem,
  pathname: string,
  searchParams: URLSearchParams,
): boolean => {
  if (!menu.children) return false;
  return menu.children.some((child) => {
    if (child.routeId) {
      const patterns = urlPathConfiguration[child.routeId]?.urlPath;
      if (isActivePatterns(patterns, pathname, searchParams)) return true;
    }
    return child.children
      ? isChildActive(child, pathname, searchParams)
      : false;
  });
};

/**
 * Given a routeId, returns true if the current location matches its
 * urlPathConfiguration patterns.
 */
export const isItemActive = (
  routeId: string | undefined,
  pathname: string,
  searchParams: URLSearchParams,
): boolean => {
  if (!routeId) return false;
  const patterns = urlPathConfiguration[routeId]?.urlPath;
  return isActivePatterns(patterns, pathname, searchParams);
};

/** Rail-to-path mapping. Mirrors Vue watchEffect (AsideMenu.vue lines 1149-1202). */
const RAIL_PATH_RULES: ReadonlyArray<{ match: string; railId: string }> = [
  { match: "/dashboard/edi", railId: "dashboard" },
  { match: "container", railId: "purchasing" },
  { match: "purchase-order", railId: "purchasing" },
  { match: "purchasing/shipment", railId: "purchasing" },
  { match: "/vendors", railId: "purchasing" },
  { match: "/vendor", railId: "purchasing" },
  { match: "/vendor-bank", railId: "purchasing" },
  { match: "orders", railId: "orders" },
  { match: "product", railId: "products" },
  { match: "materialsubtype", railId: "products" },
  { match: "jobs", railId: "jobs" },
  { match: "inventory-report", railId: "inventory" },
  { match: "accounts-receivable", railId: "accounting" },
  // React-side AR Contacts page lives at `/ar-contacts` (see
  // `APP_ROUTES.arContacts` in `src/constants/routes.ts` and the
  // menuConfig entry for the `ar-contacts` leaf). Without this rule,
  // visiting `/ar-contacts` would leave `derivedRailId` null and the
  // active-rail fallback (filteredMenu[0]) would land on Dashboard
  // instead of Accounting.
  { match: "/ar-contacts", railId: "accounting" },
  // React-side Shipping Prices page lives at `/shipping-prices` (see
  // `APP_ROUTES.shippingPrices` in `src/constants/routes.ts` and the
  // menuConfig entry for the `shipping-prices` leaf). Like `/ar-contacts`
  // above, without this rule visiting `/shipping-prices` would leave
  // `derivedRailId` null and the active-rail fallback (filteredMenu[0])
  // would land on Dashboard instead of Orders.
  { match: "/orders/shipping-prices", railId: "orders" },
  { match: "/marketing", railId: "marketing" },
];

/**
 * Determine which rail item should be active based on the current path.
 * Returns the rail item `id` or null.
 */
export const deriveActiveRailId = (pathname: string): string | null => {
  for (const rule of RAIL_PATH_RULES) {
    if (pathname.includes(rule.match)) return rule.railId;
  }
  return null;
};

/**
 * React hook that computes the full active-state model for the aside menu.
 *
 * Returns:
 * - `activeRailId`: which rail icon is highlighted
 * - `isLeafActive(item)`: whether a specific leaf item is the active route
 * - `isParentActive(item)`: whether a parent item has an active descendant
 * - `activeLeafId`: the id of the active leaf item (for disabled-link)
 */
export function useActiveMenu(filteredMenu: ReadonlyArray<MenuItem>) {
  const location = useLocation();
  const pathname = location.pathname;
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  /** Which rail item is active -- derived from path. */
  const activeRailId = useMemo(() => deriveActiveRailId(pathname), [pathname]);

  /** Find the active leaf by walking the entire menu tree. */
  const activeLeafId = useMemo(() => {
    // Per-item active-resolution. Extracted from the recursive walker to
    // reduce its cognitive complexity (S3776).
    const resolveActiveFromItem = (
      item: MenuItem,
      recurse: (items: ReadonlyArray<MenuItem>) => string | null,
    ): string | null => {
      const isItemMatched =
        !!item.routeId && isItemActive(item.routeId, pathname, searchParams);
      if (item.children) {
        const childResult = recurse(item.children);
        if (childResult) return childResult;
        // No descendant matched. A group-parent (Amazon, Baldor, GFS,
        // Sysco, US Foods, CW, …) can ALSO be the active leaf in its own
        // right — it carries its own `route`/`routeId` for its
        // group-level listing (e.g. `/orders/sales-orders/baldor`), not
        // just for its children. A self-match here IS the answer, not a
        // fallthrough: previously this branch could only ever resolve a
        // DESCENDANT, never the parent's own match, so landing on a
        // group's own page only "worked" by coincidence, via the
        // separate exact-string `findExact` pass below — which silently
        // fails for anything `isItemActive`'s pattern/UUID-stripping
        // matching would catch but a plain `route === pathname` wouldn't.
        return isItemMatched ? item.id : null;
      }
      return isItemMatched ? item.id : null;
    };

    const findActive = (items: ReadonlyArray<MenuItem>): string | null => {
      for (const item of items) {
        const result = resolveActiveFromItem(item, findActive);
        if (result) return result;
      }
      return null;
    };
    // Backstop for a React leaf whose own exact pathname matches but whose
    // routeId/urlPathConfiguration pattern isn't registered (or doesn't
    // cover this exact path) — `resolveActiveFromItem` above is now the
    // primary mechanism and already handles the common self-match case.
    const findExact = (items: ReadonlyArray<MenuItem>): string | null => {
      for (const item of items) {
        if (item.isReactRoute && item.route === pathname) return item.id;
        if (item.children) {
          const r = findExact(item.children);
          if (r) return r;
        }
      }
      return null;
    };
    return findActive(filteredMenu) ?? findExact(filteredMenu);
  }, [filteredMenu, pathname, searchParams]);

  /** Check if a specific item is the active leaf. */
  const isLeafActive = useMemo(() => {
    return (item: MenuItem): boolean => item.id === activeLeafId;
  }, [activeLeafId]);

  /** Check if a parent item has an active descendant. */
  const isParentActive = useMemo(() => {
    return (item: MenuItem): boolean => {
      if (!item.children) return false;
      return isChildActive(item, pathname, searchParams);
    };
  }, [pathname, searchParams]);

  /** Set of ancestor IDs of the active leaf -- for auto-expanding. */
  const activeAncestorIds = useMemo(() => {
    if (!activeLeafId) return new Set<string>();
    const ids = new Set<string>();
    const walk = (
      items: ReadonlyArray<MenuItem>,
      ancestors: string[],
    ): boolean => {
      for (const item of items) {
        if (item.id === activeLeafId) {
          ancestors.forEach((a) => ids.add(a));
          return true;
        }
        if (item.children && walk(item.children, [...ancestors, item.id])) {
          return true;
        }
      }
      return false;
    };
    walk(filteredMenu, []);
    return ids;
  }, [filteredMenu, activeLeafId]);

  return {
    activeRailId,
    activeLeafId,
    isLeafActive,
    isParentActive,
    activeAncestorIds,
  } as const;
}
